import { FC, useCallback, useEffect, useState } from 'react';

import { PencilAltIcon, XIcon } from '@heroicons/react/outline';
import { getGiftAgentOrder } from '@shared/utils/giftAgentOrder.util';

/**
 * 전화 상담 메모장 (전화주문 관리 + 대리주문 스토어 공용).
 *
 * 고객이 말하는 걸 받아적는 용도. 상담 중 화면을 오가도(고객검색 → 스토어 →
 * 입금 확인) 같은 내용이 이어져야 해서 localStorage 한 곳에 저장한다.
 * 서버에 올리지 않는다 — 담당자 개인 메모이고, 브라우저 안에서만 남는다.
 */

const MEMO_KEY = 'giftPhoneMemo.text';
const OPEN_KEY = 'giftPhoneMemo.open';

interface Props {
  /** 하단에 고정 바가 있는 화면(장바구니·결제)에서 결제 버튼을 가리지 않게 띄운다 */
  raised?: boolean;
}

export const GiftPhoneMemoPad: FC<Props> = function GiftPhoneMemoPad({ raised }) {
  const bottomClass = raised ? 'bottom-24' : 'bottom-4';
  const [text, setText] = useState(() => localStorage.getItem(MEMO_KEY) ?? '');
  const [isOpen, setIsOpen] = useState(
    () => sessionStorage.getItem(OPEN_KEY) === '1',
  );
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  // 상담 중인 고객 (대리주문 진행 중일 때만) — 누구 메모인지 헷갈리지 않게 표시
  const agentOrder = getGiftAgentOrder();

  useEffect(() => {
    sessionStorage.setItem(OPEN_KEY, isOpen ? '1' : '0');
  }, [isOpen]);

  const handleChange = useCallback((value: string) => {
    setText(value);
    localStorage.setItem(MEMO_KEY, value);
    setSavedAt(new Date());
  }, []);

  const handleClear = useCallback(() => {
    if (!window.confirm('메모를 모두 지웁니다.')) return;
    setText('');
    localStorage.removeItem(MEMO_KEY);
    setSavedAt(null);
  }, []);

  const handleCopy = useCallback(() => {
    if (!text) return;
    navigator.clipboard?.writeText(text);
  }, [text]);

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed ${bottomClass} left-4 z-40 flex items-center gap-1 rounded-full bg-gray-800 px-4 py-2 text-xs font-medium text-white shadow-lg hover:bg-gray-700`}
      >
        <PencilAltIcon className="h-4 w-4" />
        메모
        {text.trim() && (
          <span className="ml-0.5 h-1.5 w-1.5 rounded-full bg-amber-300" />
        )}
      </button>
    );
  }

  return (
    <div
      className={`fixed ${bottomClass} left-4 z-40 flex w-80 max-w-[calc(100vw-2rem)] flex-col rounded-lg border border-gray-300 bg-white shadow-xl`}
    >
      <div className="flex items-center gap-2 border-b border-gray-200 px-3 py-2">
        <PencilAltIcon className="h-4 w-4 text-gray-500" />
        <p className="text-sm font-semibold text-gray-800">상담 메모</p>
        {agentOrder && (
          <span className="truncate text-xs text-gray-500">
            {agentOrder.memberName}님
          </span>
        )}
        <button
          onClick={() => setIsOpen(false)}
          className="ml-auto rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          title="접기"
        >
          <XIcon className="h-4 w-4" />
        </button>
      </div>
      <textarea
        value={text}
        onChange={(e) => handleChange(e.target.value)}
        placeholder={
          '통화 내용을 적어두세요.\n' +
          '예) 30개, 감사글 “깊이 감사드립니다”, 배송지는 자택'
        }
        className="h-48 w-full resize-y border-0 p-3 text-sm focus:outline-none focus:ring-0"
      />
      <div className="flex items-center gap-2 border-t border-gray-200 px-3 py-2">
        <span className="text-xs text-gray-400">
          {savedAt ? '자동 저장됨' : '이 브라우저에만 저장됩니다'}
        </span>
        <button
          onClick={handleCopy}
          disabled={!text}
          className="ml-auto rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40"
        >
          복사
        </button>
        <button
          onClick={handleClear}
          disabled={!text}
          className="rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40"
        >
          지우기
        </button>
      </div>
    </div>
  );
};
